import React from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FlagIcon from "@mui/icons-material/Flag";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PublicIcon from "@mui/icons-material/Public";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import ComputerIcon from "@mui/icons-material/Computer";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const AboutIGI = () => {
  return (
    <section
      id="about-igi"
      className="relative py-24 xs:py-12 xxs:py-12 px-6 xs:px-8 xxs:px-8 md:px-12 md:py-[3rem] bg-brandBlue text-white font-outfit overflow-hidden"
    >
      <div className="max-w-6xl mx-auto relative z-10">
        <h3 className="text-[1.3125rem] text-center xxs:text-lg xs:text-lg xxs:mb-[1.5rem] xs:mb-[1.5rem] leading-[1.3125rem] mb-[3rem] uppercase tracking-[0.2em] font-normal text-white">
          About International Governance Institute
        </h3>

        <div className="text-sm md:text-xl max-w-[76%] xs:max-w-full xxs:max-w-full flex flex-col gap-[2rem] leading-relaxed">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <AccountBalanceIcon sx={{ fontSize: 32, color: "#00f78f" }} />
              <h2 className="text-2xl font-bold text-softGreen">
                International Governance Institute (IGI)
              </h2>
            </div>
            <p className="text-lg font-semibold text-[#B8EAE4] mb-6">
              "Addressing Global Governance Challenges & Advancing Reform,
              Decentralization & European Integration"
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FlagIcon sx={{ fontSize: 28, color: "#00f78f" }} />
              <h3 className="text-xl font-bold text-softGreen">
                Mission Statement
              </h3>
            </div>
            <p className="mb-4">
              The International Governance Institute is dedicated to addressing
              today's global governance challenges and advancing reform,
              decentralization, and European integration in the Western Balkans
              and beyond.
            </p>
            <p className="mb-4">
              We explore the evolving landscape of international governance
              through three central imperatives:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>
                Reasserting the role of the public sector in an era increasingly
                dominated by private actors.
              </li>
              <li>
                Rethinking the limits of post–Cold War "Good Governance"
                principles, which require new approaches to meet today's
                realities.
              </li>
              <li>
                Acknowledging the rising leadership of host nations in shaping
                and owning their reforms, often under the auspices of
                multilateral actors such as the World Bank.
              </li>
            </ul>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <VisibilityIcon sx={{ fontSize: 28, color: "#00f78f" }} />
              <h3 className="text-xl font-bold text-softGreen">
                Our Reform Principles
              </h3>
            </div>
            <p className="mb-4">
              Our work is guided by four principles of reform:
            </p>
            <ol className="list-decimal list-inside mb-4 space-y-2">
              <li>
                Confidence through competence as the foundation of public trust.
              </li>
              <li>
                Confronting corruption as the universal obstacle to progress.
              </li>
              <li>
                Building coalitions where compromise is reframed as a stepping
                stone to shared goals and not as concession.
              </li>
              <li>
                Sustaining hope to secure long-term citizen engagement across
                generations.
              </li>
            </ol>
            <p>
              Through 21 targeted development interventions, the Institute
              promotes transparent governance, institutional resilience, and
              citizen trust, helping societies adapt to new global realities
              while safeguarding their public mandate.
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <PublicIcon sx={{ fontSize: 28, color: "#00f78f" }} />
              <h3 className="text-xl font-bold text-softGreen">What We Do</h3>
            </div>
            <p className="mb-4">
              The Institute contributes to democratic governance, stability, and
              sustainable development through the following pillars of action:
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUpIcon sx={{ fontSize: 28, color: "#00f78f" }} />
              <h3 className="text-xl font-bold text-softGreen">
                Our Pillars of Action
              </h3>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AdminPanelSettingsIcon
                    sx={{ fontSize: 24, color: "#B8EAE4" }}
                  />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    1. Public Administration Reform
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Institutional and functional reforms for a modern, efficient
                    public administration.
                  </li>
                  <li>
                    Promoting a merit-based and professional civil service.
                  </li>
                  <li>Enhancing intergovernmental coordination.</li>
                  <li>
                    Aligning laws and policies with EU acquis (Chapters 23 &
                    24).
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <LocationCityIcon sx={{ fontSize: 24, color: "#B8EAE4" }} />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    2. Good Governance & Economic Development for European
                    Integration
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Supporting effective governance as a driver of economic
                    growth.
                  </li>
                  <li>
                    Advancing employment policies and investment opportunities.
                  </li>
                  <li>
                    Engaging the private sector in regional development (EU
                    Chapter 22).
                  </li>
                  <li>
                    Combating corruption as a foundation for EU membership.
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ComputerIcon sx={{ fontSize: 24, color: "#B8EAE4" }} />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    3. Decentralized Governance Reform
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Designing and implementing fiscal and functional frameworks
                    for decentralization.
                  </li>
                  <li>
                    Strengthening local governments to deliver EU-standard
                    services.
                  </li>
                  <li>Promoting multi-level governance and subsidiarity.</li>
                  <li>
                    Implementing regional programs, such as the Western Balkans
                    Decentralized Governance Reform Program.
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <SchoolIcon sx={{ fontSize: 24, color: "#B8EAE4" }} />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    4. E-Governance & Digital Transformation
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>Developing interoperable e-governance systems.</li>
                  <li>Promoting open data and transparency platforms.</li>
                  <li>Training public officials in digital skills.</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AdminPanelSettingsIcon
                    sx={{ fontSize: 24, color: "#B8EAE4" }}
                  />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    5. Governance Capacity & Reform Leadership
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Establishing the EU Reform Leadership Academy for senior
                    officials and professionals.
                  </li>
                  <li>Creating a Regional Network of Reform Champions.</li>
                  <li>
                    Supporting EU–Western Balkans twinning initiatives for
                    best-practice exchange.
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FlagIcon sx={{ fontSize: 24, color: "#B8EAE4" }} />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    6. Human & Institutional Security
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Policies for human, democratic, and institutional security.
                  </li>
                  <li>
                    Programs for crisis management, community safety, and public
                    integrity.
                  </li>
                  <li>
                    Promoting regional cooperation in democratic security.
                  </li>
                  <li>
                    Advancing digital security, data protection, and countering
                    disinformation.
                  </li>
                  <li>
                    Supporting regional stability through resilient reforms.
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <PublicIcon sx={{ fontSize: 24, color: "#B8EAE4" }} />
                  <h4 className="text-lg font-semibold text-[#B8EAE4]">
                    7. Supporting & Promotional Activities
                  </h4>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Organizing conferences, forums, training, and publications.
                  </li>
                  <li>Conducting research and policy recommendations.</li>
                  <li>
                    Building partnerships with governments, academia, and
                    international organizations.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="relative pl-5 border-l-[4px] mt-10 border-transparent">
            <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#00f78f] to-softGreen rounded-sm" />
            <p className="font-semibold text-lg text-[#B8EAE4] ml-2">
              Why IGI?
            </p>
            <p className="ml-2 mt-2">
              IGI brings deep expertise in governance innovation, public sector
              reform, and EU-aligned policy advice – making it a trusted partner
              for governments and the EU to deliver transformative governance
              change.
            </p>
          </div>
        </div>
      </div>
      <div className="absolute top-[-100px] left-[-100px] w-[300px] h-[300px] bg-softGreen/30 blur-3xl rounded-full z-0" />
      <div className="absolute bottom-[-100px] right-[-100px] w-[300px] h-[300px] bg-brandGreen/20 blur-3xl rounded-full z-0" />
    </section>
  );
};

export default AboutIGI;
